import { useEffect, useRef, useState } from 'react';

/**
 * A rich-text block that stops at a few lines with a Show more / Show less
 * toggle underneath.
 *
 * Written for the item dialog, where the description competes for space with
 * the price, the stock and the add-to-cart button — a supplier's ten-bullet
 * feature list pushed all of those below the fold, and at a register the thing
 * below the fold is the button the cashier came for.
 *
 * The toggle only appears when the text ACTUALLY overflows. Measuring is the
 * only way to know that: the caller cannot tell from the markup whether a
 * description is one line or thirty, and a Show more that expands nothing is
 * worse than no button.
 *
 * The height is read from the INNER element, which is never clamped — the outer
 * one is capped at the collapsed height, so asking it would just return the cap.
 */
export function CollapsibleHtml({
  html,
  collapsedPx = 132,
  moreLabel,
  lessLabel,
  className = '',
}: {
  /** Already sanitized. This component does not clean anything. */
  html: string;
  collapsedPx?: number;
  moreLabel: string;
  lessLabel: string;
  className?: string;
}): JSX.Element {
  const inner = useRef<HTMLDivElement>(null);
  const [expanded, setExpanded] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = inner.current;
    if (!el) return undefined;
    const measure = (): void => setOverflows(el.offsetHeight > collapsedPx + 8);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    // Fonts and images land after the first paint and change the height, so a
    // single measurement on mount decides from a layout that is not final yet.
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [html, collapsedPx]);

  // A soft fade over the cut, so it reads as "there is more" rather than as a
  // description that happens to end mid-sentence. A mask rather than a
  // gradient overlay: it fades the text itself, so it needs to know nothing
  // about whatever the surface behind it is coloured.
  const clipped = !expanded && overflows;
  const fade = 'linear-gradient(to bottom, black 60%, transparent 100%)';

  return (
    <div className={className}>
      <div
        style={{
          ...(clipped ? { maxHeight: collapsedPx, overflow: 'hidden' } : {}),
          ...(clipped ? { maskImage: fade, WebkitMaskImage: fade } : {}),
        }}
      >
        <div ref={inner} className="rte-content text-sm leading-relaxed text-fg-muted" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
      {overflows && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="mt-1 text-xs font-semibold text-brand underline-offset-4 hover:underline focus:outline-none focus-visible:underline"
        >
          {expanded ? lessLabel : moreLabel}
        </button>
      )}
    </div>
  );
}
